/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import * as jose from "jose"
import * as oauth2 from "oauth4webapi"
import { TokenEndpointResponse } from "oauth4webapi"
import { getRedirectURI } from "@/utils/auth"
import { atomWithStorage } from "jotai/utils"
import { safeJSONLocalStorage, safeLocalStorage } from "@/utils/safe-jotai-storage"
import { store } from "@/store/store"
import { Sentry } from "@/telemetry/sentry"
import { atom } from "jotai"
import { analytics, deactivateAmplitudeUntilNextUser } from "@/telemetry/amplitude"

// Define environment variables
const OAUTH_SERVER_BASE_URI = import.meta.env.VITE_OAUTH_SERVER_BASE_URI
const OAUTH_CLIENT_ID = import.meta.env.VITE_OAUTH_CLIENT_ID

// Define constants
const TOKEN_EXPIRY_BUFFER_SECONDS = 120
// Persist PKCE verifiers by OAuth state to avoid cross-tab overwrites.
const PKCE_STORAGE_KEY = "pixieset-pkce"
const PKCE_SESSION_KEY = "pixieset-pkce-session"
// Keep verifiers briefly to survive navigation, but not indefinitely.
const PKCE_TTL_MS = 15 * 60 * 1000
// Expiry leaves a tombstone behind so a late callback can still be recognised as
// a TTL drop rather than an unknown state. Outlives PKCE_TTL_MS by enough to
// cover a slow provider round-trip; the verifier itself is discarded on expiry.
const PKCE_TOMBSTONE_TTL_MS = 24 * 60 * 60 * 1000
const MAX_PKCE_TOMBSTONES = 20
const SAFE_URL_QUERY_KEYS = new Set(["error", "error_description", "error_uri", "iss"])

type PkceVerifierEntry = {
	verifier: string
	createdAt: number
}
// A state whose verifier expired and was discarded.
type PkceTombstone = {
	expiredAt: number
}
type PkceEntry = PkceVerifierEntry | PkceTombstone
type PkceStore = Record<string, PkceEntry>

const isVerifierEntry = (entry: PkceEntry | undefined): entry is PkceVerifierEntry =>
	!!entry &&
	"verifier" in entry &&
	typeof entry.verifier === "string" &&
	"createdAt" in entry &&
	typeof entry.createdAt === "number"

const isTombstone = (entry: PkceEntry | undefined): entry is PkceTombstone =>
	!!entry && "expiredAt" in entry && typeof entry.expiredAt === "number"

export interface OAuth2Token extends TokenEndpointResponse {
	/**
	 * JWT claims retrieved by decoding the access token
	 */
	sub: number // User ID
	nbf: number // Not Before
	exp: number // Expiration
}
const client: oauth2.Client = {
	client_id: OAUTH_CLIENT_ID,
	token_endpoint_auth_method: "none",
}
const issuer: URL = new URL(OAUTH_SERVER_BASE_URI)
let authorizationServer: oauth2.AuthorizationServer | null = null

const getStoredAccessToken = (): OAuth2Token | null => {
	const stored = safeLocalStorage.getItem("pixieset-token")
	if (!stored) {
		return null
	}

	try {
		const parsed = JSON.parse(stored)
		return parsed && typeof parsed === "object" ? (parsed as OAuth2Token) : null
	} catch {
		return null
	}
}

const getStoredReturnedTo = (): string | null => {
	const stored = safeLocalStorage.getItem("pixieset-return-to")
	if (!stored) {
		return null
	}

	// Support both legacy plain-string storage and JSON-string storage.
	try {
		const parsed = JSON.parse(stored)
		return typeof parsed === "string" ? parsed : null
	} catch {
		return stored.replace(/(^"|"$)/g, "")
	}
}

const accessTokenAtom = atomWithStorage<OAuth2Token | null>(
	"pixieset-token",
	getStoredAccessToken(),
	safeJSONLocalStorage<OAuth2Token | null>(),
)
const returnToStorageAtom = atomWithStorage<string | null>(
	"pixieset-return-to",
	getStoredReturnedTo(),
	safeJSONLocalStorage<string | null>(),
)

const returnToAtom = atom(
	(get) => get(returnToStorageAtom),
	(_, set, newValue: string | null) => {
		if (newValue === null) {
			try {
				localStorage.removeItem("pixieset-return-to")
			} catch {
				// ignore
			}
		} else {
			set(returnToStorageAtom, newValue)
		}
	},
)

const isRefreshingTokenAtom = atom(false)
let refreshPromise: Promise<string> | null = null
const isPSAuthenticatedAtom = atom((get) => {
	return get(accessTokenAtom) !== null
})

// Defensive JSON parse to protect against invalid storage contents.
const parsePkceStore = (raw: string | null): PkceStore => {
	if (!raw) return {}
	try {
		const parsed = JSON.parse(raw) as PkceStore
		if (!parsed || typeof parsed !== "object") return {}
		return parsed
	} catch {
		return {}
	}
}

// Drop malformed entries; replace expired verifiers with a tombstone so a late
// callback is still classified as a TTL drop instead of an unknown state.
const prunePkceStore = (storeData: PkceStore): PkceStore => {
	const now = Date.now()
	const next: PkceStore = {}
	const tombstones: Array<[string, PkceTombstone]> = []

	const keepTombstone = (state: string, tombstone: PkceTombstone) => {
		if (now - tombstone.expiredAt > PKCE_TOMBSTONE_TTL_MS) return
		tombstones.push([state, tombstone])
	}

	for (const [state, entry] of Object.entries(storeData)) {
		if (isTombstone(entry)) {
			// Re-mint rather than copy: a malformed entry carrying extra fields
			// (e.g. a verifier) must not survive as part of the tombstone.
			keepTombstone(state, { expiredAt: entry.expiredAt })
			continue
		}
		if (!isVerifierEntry(entry)) {
			continue
		}
		if (now - entry.createdAt <= PKCE_TTL_MS) {
			next[state] = entry
		} else {
			keepTombstone(state, { expiredAt: entry.createdAt + PKCE_TTL_MS })
		}
	}

	// Bound growth: a login loop must not grow storage without limit.
	tombstones.sort((a, b) => a[1].expiredAt - b[1].expiredAt)
	for (const [state, tombstone] of tombstones.slice(-MAX_PKCE_TOMBSTONES)) {
		next[state] = tombstone
	}

	return next
}

const readPkceStore = (storage: Storage, key: string): PkceStore => {
	const raw = storage.getItem(key)
	return prunePkceStore(parsePkceStore(raw))
}

const getSafeUrlContext = (url: URL) => {
	const safeQuery: Record<string, string> = {}
	for (const key of SAFE_URL_QUERY_KEYS) {
		const value = url.searchParams.get(key)
		if (value) {
			safeQuery[key] = value.length > 120 ? `${value.slice(0, 120)}…` : value
		}
	}

	return {
		origin: url.origin,
		pathname: url.pathname,
		hasCode: url.searchParams.has("code"),
		hasState: url.searchParams.has("state"),
		hasHash: url.hash.length > 0,
		query: safeQuery,
	}
}

const buildOAuthErrorContext = (result: oauth2.OAuth2Error) => {
	return {
		error: typeof result.error === "string" ? result.error : "unknown",
		error_description:
			typeof result.error_description === "string" ? result.error_description : undefined,
		error_uri: typeof result.error_uri === "string" ? result.error_uri : undefined,
	}
}

const isTokenEndpointResponse = (result: unknown): result is oauth2.TokenEndpointResponse => {
	return (
		!!result &&
		typeof result === "object" &&
		"access_token" in result &&
		typeof result.access_token === "string"
	)
}

const isOAuthErrorResponse = (result: unknown): result is oauth2.OAuth2Error => {
	return (
		!!result &&
		typeof result === "object" &&
		"error" in result &&
		typeof result.error === "string"
	)
}

export type PSAccessTokenRefreshFailureReason =
	| "expired"
	| "revoked"
	| "missing_refresh_token"
	| "invalid_refresh_token"
	| "network"
	| "timeout"
	| "discovery"
	| "oauth_server"
	| "oauth_error"
	| "invalid_response"

const TRANSIENT_REFRESH_FAILURE_REASONS = new Set<PSAccessTokenRefreshFailureReason>([
	"network",
	"timeout",
	"discovery",
	"oauth_server",
	"invalid_response",
])

export class PSAccessTokenRefreshError extends Error {
	public readonly reason: PSAccessTokenRefreshFailureReason
	public readonly isTransient: boolean

	public constructor(reason: PSAccessTokenRefreshFailureReason, options?: ErrorOptions) {
		super("Pixieset access token refresh failed", options)
		this.name = "PSAccessTokenRefreshError"
		this.reason = reason
		this.isTransient = TRANSIENT_REFRESH_FAILURE_REASONS.has(reason)
	}
}

export const isPSAccessTokenRefreshError = (error: unknown): error is PSAccessTokenRefreshError =>
	error instanceof PSAccessTokenRefreshError

export type PSAccessTokenRequestContext = {
	flow: "photo_editor_api" | "pixieset_api"
	method?: string
	path?: string
}

type RefreshFailureContext = {
	error?: unknown
	oauthError?: string
	request?: PSAccessTokenRequestContext
}

const reportRefreshFailure = (
	reason: PSAccessTokenRefreshFailureReason,
	context: RefreshFailureContext = {},
) => {
	const error = new PSAccessTokenRefreshError(reason, { cause: context.error })
	const cause = context.error instanceof Error ? context.error : undefined
	Sentry.captureMessage("[OAuthClient] Pixieset access token refresh failed", {
		level: error.isTransient ? "warning" : "info",
		fingerprint: ["pixieset-access-token-refresh-failure"],
		tags: {
			auth_refresh_failure_reason: reason,
			auth_refresh_failure_transient: String(error.isTransient),
			auth_refresh_request_flow: context.request?.flow,
			auth_refresh_request_method: context.request?.method,
		},
		extra: {
			errorName: cause?.name,
			errorMessage: cause?.message.slice(0, 200),
			oauthError: context.oauthError,
			requestPath: context.request?.path,
		},
	})
	return error
}

const classifyTokenRequestError = (
	error: unknown,
): Extract<PSAccessTokenRefreshFailureReason, "network" | "timeout" | "invalid_response"> => {
	if (error instanceof Error && (error.name === "TimeoutError" || error.name === "AbortError")) {
		return "timeout"
	}

	if (
		error instanceof Error &&
		/network|failed to fetch|load failed|offline/i.test(error.message)
	) {
		return "network"
	}

	return "invalid_response"
}

const classifyOAuthRefreshError = (
	result: oauth2.OAuth2Error,
): PSAccessTokenRefreshFailureReason => {
	const hint = typeof result.hint === "string" ? result.hint : ""
	if (hint === "Token has expired") return "expired"
	if (hint === "Token has been revoked") return "revoked"
	if (hint === "Cannot decrypt the refresh token" || result.error === "invalid_grant") {
		return "invalid_refresh_token"
	}
	if (result.error === "server_error" || result.error === "temporarily_unavailable") {
		return "oauth_server"
	}
	return "oauth_error"
}

const writePkceStore = (storage: Storage, key: string, storeData: PkceStore) => {
	const hasEntries = Object.keys(storeData).length > 0
	if (!hasEntries) {
		storage.removeItem(key)
		return
	}
	storage.setItem(key, JSON.stringify(storeData))
}

// Save to both localStorage and sessionStorage for resilience.
const savePkceVerifier = (state: string, verifier: string) => {
	const entry: PkceVerifierEntry = { verifier, createdAt: Date.now() }
	const localStore = readPkceStore(localStorage, PKCE_STORAGE_KEY)
	localStore[state] = entry
	writePkceStore(localStorage, PKCE_STORAGE_KEY, localStore)

	const sessionStore = readPkceStore(sessionStorage, PKCE_SESSION_KEY)
	sessionStore[state] = entry
	writePkceStore(sessionStorage, PKCE_SESSION_KEY, sessionStore)
}

// Read-once to prevent replay; prefer sessionStorage. Tombstones are left in
// place so a repeat callback stays classifiable as an expiry.
const consumePkceVerifier = (state: string): string | null => {
	const sessionStore = readPkceStore(sessionStorage, PKCE_SESSION_KEY)
	const sessionEntry = sessionStore[state]
	if (isVerifierEntry(sessionEntry)) {
		delete sessionStore[state]
		writePkceStore(sessionStorage, PKCE_SESSION_KEY, sessionStore)

		const localStore = readPkceStore(localStorage, PKCE_STORAGE_KEY)
		if (isVerifierEntry(localStore[state])) {
			delete localStore[state]
			writePkceStore(localStorage, PKCE_STORAGE_KEY, localStore)
		}
		return sessionEntry.verifier
	}

	const localStore = readPkceStore(localStorage, PKCE_STORAGE_KEY)
	const localEntry = localStore[state]
	if (isVerifierEntry(localEntry)) {
		delete localStore[state]
		writePkceStore(localStorage, PKCE_STORAGE_KEY, localStore)
		return localEntry.verifier
	}

	// Persist any tombstones minted while pruning, so the expiry survives.
	writePkceStore(sessionStorage, PKCE_SESSION_KEY, sessionStore)
	writePkceStore(localStorage, PKCE_STORAGE_KEY, localStore)
	return null
}

// True if `state` was saved and has since expired — either a tombstone left by
// pruning, or an entry past PKCE_TTL_MS that has not been pruned yet. Lets
// callers tell a TTL drop (recoverable, AppAuthGuard retries) from an entry
// that was never saved or already consumed (real signal worth capturing).
const wasPkceEntryExpired = (state: string): boolean => {
	const sessionRaw = parsePkceStore(sessionStorage.getItem(PKCE_SESSION_KEY))
	const localRaw = parsePkceStore(localStorage.getItem(PKCE_STORAGE_KEY))
	const entry = sessionRaw[state] ?? localRaw[state]
	if (isTombstone(entry)) return true
	if (!isVerifierEntry(entry)) return false
	return Date.now() - entry.createdAt > PKCE_TTL_MS
}

// Used on explicit logout/reset only.
const clearPkceStore = () => {
	localStorage.removeItem(PKCE_STORAGE_KEY)
	sessionStorage.removeItem(PKCE_SESSION_KEY)
}

const DISCOVERY_MAX_RETRIES = 2
const DISCOVERY_RETRY_DELAY_MS = 1000
// Per-request timeout so a hung OAuth server can't stall login indefinitely.
const OAUTH_REQUEST_TIMEOUT_MS = 8_000

const discoverAuthorizationServer = async (options?: { reportFailure?: boolean }) => {
	if (authorizationServer !== null) {
		return
	}

	let lastError: unknown
	for (let attempt = 0; attempt <= DISCOVERY_MAX_RETRIES; attempt++) {
		try {
			const response = await oauth2.discoveryRequest(issuer, {
				algorithm: "oauth2",
				signal: AbortSignal.timeout(OAUTH_REQUEST_TIMEOUT_MS),
			})
			authorizationServer = await oauth2.processDiscoveryResponse(issuer, response)
			return
		} catch (error) {
			lastError = error
			if (attempt < DISCOVERY_MAX_RETRIES) {
				await new Promise((r) => setTimeout(r, DISCOVERY_RETRY_DELAY_MS * (attempt + 1)))
			}
		}
	}

	console.error("[OAuthClient] discoverAuthorizationServer() Error:", lastError)
	// Surface the cause by name (raw Errors serialize to {} in Sentry extra) and
	// tag it to split network failures from upstream failures.
	const cause = lastError instanceof Error ? lastError : new Error(String(lastError))
	if (options?.reportFailure !== false) {
		Sentry.captureException(
			new Error("[OAuthClient] discoverAuthorizationServer(): Discovery request failed", {
				cause,
			}),
			{
				captureContext: {
					extra: {
						errorName: cause.name,
						errorMessage: cause.message,
						attempts: DISCOVERY_MAX_RETRIES + 1,
					},
					tags: { discovery_failure: cause.name },
				},
			},
		)
	}
	throw new Error("OAuth discovery failed", { cause: lastError })
}

const setPSAccessToken = (newToken: oauth2.TokenEndpointResponse | OAuth2Token) => {
	/**
	 * Decode the JWT to get the claims
	 */
	let claims: object & jose.JWTPayload = {}
	try {
		claims = jose.decodeJwt(newToken.access_token)
	} catch (e) {
		logoutFromPS()
	}

	const expiresIn = newToken.expires_in
	const exp =
		typeof expiresIn === "number" && Number.isFinite(expiresIn) && expiresIn > 0
			? Math.floor(Date.now() / 1000) + expiresIn
			: (claims.exp ?? 0)

	/**
	 * Build the access token object
	 */
	const token: OAuth2Token = {
		...newToken,
		sub: claims.sub ? parseInt(claims.sub, 10) : 0, // User ID
		nbf: claims.nbf ?? 0, // Not before
		exp, // Expiration
	}

	store.set(accessTokenAtom, token)
}

const clearPSAccessToken = () => {
	store.set(accessTokenAtom, null)
}

const refreshPSAccessToken = async (request?: PSAccessTokenRequestContext): Promise<string> => {
	store.set(isRefreshingTokenAtom, true)

	try {
		const accessToken = store.get(accessTokenAtom)
		const refreshToken = accessToken?.refresh_token
		if (refreshToken === undefined) {
			clearPSAccessToken()
			throw reportRefreshFailure("missing_refresh_token", { request })
		}

		try {
			await discoverAuthorizationServer({ reportFailure: false })
		} catch (error) {
			throw reportRefreshFailure("discovery", { error, request })
		}

		let result: unknown
		try {
			const response = await oauth2.refreshTokenGrantRequest(
				authorizationServer as oauth2.AuthorizationServer,
				client,
				refreshToken,
				{ signal: AbortSignal.timeout(OAUTH_REQUEST_TIMEOUT_MS) },
			)
			result = await response.json()
		} catch (error) {
			throw reportRefreshFailure(classifyTokenRequestError(error), { error, request })
		}
		if (isOAuthErrorResponse(result)) {
			/**
			 * If the refresh token is "rosa", it means we have "dogeified" the refresh token
			 * in the ApplicationDevInfoModal, for testing purposes, and we should not log an error
			 */
			if (refreshToken !== "rosa") {
				console.error("[OAuthClient] refreshAccessToken() Error: Token refresh failed", {
					error: result.error,
					hint: result.hint,
				})
			}

			const refreshError = reportRefreshFailure(classifyOAuthRefreshError(result), {
				oauthError: result.error,
				request,
			})
			if (
				!refreshError.isTransient &&
				getStoredAccessToken()?.refresh_token === refreshToken
			) {
				clearPSAccessToken()
			}
			throw refreshError
		}

		if (!isTokenEndpointResponse(result)) {
			throw reportRefreshFailure("invalid_response", { request })
		}

		setPSAccessToken(result)
		return result.access_token
	} finally {
		store.set(isRefreshingTokenAtom, false)
	}
}

const isTokenValid = (token: OAuth2Token | null): token is OAuth2Token => {
	if (token === null) {
		return false
	}

	/**
	 * Check if the access token will expire within TOKEN_EXPIRY_BUFFER_SECONDS seconds.
	 * If it will, we want to proactively refresh the token.
	 */
	const currentTime = Math.floor(Date.now() / 1000)
	const tokenExp = Math.floor(token.exp)
	const timeUntilExpiry = tokenExp - currentTime

	return timeUntilExpiry > TOKEN_EXPIRY_BUFFER_SECONDS
}

const getPSAccessToken = async (request?: PSAccessTokenRequestContext): Promise<string> => {
	// If the current access token is valid, return it
	const accessToken = store.get(accessTokenAtom)
	if (isTokenValid(accessToken)) {
		return accessToken.access_token
	}

	/**
	 * Token is not valid so we need to refresh it.
	 * If we are not already refreshing the token, then start the refresh process now.
	 */
	if (refreshPromise === null) {
		const pendingRefresh = refreshPSAccessToken(request)
		refreshPromise = pendingRefresh
		void pendingRefresh.then(
			() => {
				if (refreshPromise === pendingRefresh) refreshPromise = null
			},
			() => {
				if (refreshPromise === pendingRefresh) refreshPromise = null
			},
		)
	}

	return refreshPromise
}

const getAuthorizationUrl = async (): Promise<string> => {
	await discoverAuthorizationServer()

	if (authorizationServer === null) {
		throw new Error("Authorization server not discovered")
	}

	const codeChallengeMethod = "S256"

	if (
		authorizationServer?.code_challenge_methods_supported?.includes(codeChallengeMethod) !==
		true
	) {
		throw new Error("Authorization server does not support S256 code challenge method")
	}

	const codeVerifier = oauth2.generateRandomCodeVerifier()
	// Bind verifier to a unique state to avoid cross-tab collisions.
	const state = oauth2.generateRandomState()
	savePkceVerifier(state, codeVerifier)

	const codeChallenge = await oauth2.calculatePKCECodeChallenge(codeVerifier)

	const authorizationUrl = new URL(authorizationServer?.authorization_endpoint as string)
	authorizationUrl.searchParams.set("client_id", client.client_id)
	authorizationUrl.searchParams.set("code_challenge", codeChallenge)
	authorizationUrl.searchParams.set("code_challenge_method", codeChallengeMethod)
	authorizationUrl.searchParams.set("redirect_uri", getRedirectURI())
	authorizationUrl.searchParams.set("response_type", "code")
	authorizationUrl.searchParams.set("state", state)
	authorizationUrl.searchParams.set("prompt", "consent")
	// authorizationUrl.searchParams.set('scope', 'workspace user studio-manager'); // Disabled until we determine whether or not to use Scopes

	return new Promise((resolve) => {
		resolve(authorizationUrl.href)
	})
}

const requestPSAccessToken = async (currentUrl: URL) => {
	await discoverAuthorizationServer()

	// Validate auth response against the state we generated.
	const state = currentUrl.searchParams.get("state")
	if (!state) {
		// Missing state indicates an older client or stale callback; require re-login.
		throw new Error("Missing state")
	}

	const parameters = oauth2.validateAuthResponse(
		authorizationServer as oauth2.AuthorizationServer,
		client,
		currentUrl,
		state,
	)

	if (oauth2.isOAuth2Error(parameters)) {
		console.error(
			"[OAuthClient] requestPSAccessToken() Auth response validation failed",
			parameters,
		)
		Sentry.captureException(
			new Error("[OAuthClient] requestPSAccessToken(): Auth response validation failed"),
			{
				captureContext: {
					extra: {
						currentUrl: getSafeUrlContext(currentUrl),
						parameters: JSON.stringify(parameters),
					},
				},
			},
		)

		throw new Error("Auth response validation failed")
	}

	// Atomically consume the PKCE verifier to prevent race conditions.
	// This replaces the previous hasPkceState() check + later consumePkceVerifier() pattern
	// which was vulnerable to race conditions between concurrent callbacks.
	const codeVerifier = consumePkceVerifier(state)
	if (codeVerifier === null) {
		if (!wasPkceEntryExpired(state)) {
			Sentry.captureException(
				new Error(
					"[OAuthClient] requestPSAccessToken(): Code verifier not found (unknown or already consumed state)",
				),
				{
					captureContext: {
						extra: {
							currentUrl: getSafeUrlContext(currentUrl),
						},
					},
				},
			)
		}
		throw new Error("Unknown or expired state")
	}

	const response = await oauth2.authorizationCodeGrantRequest(
		authorizationServer as oauth2.AuthorizationServer,
		client,
		parameters,
		getRedirectURI(),
		codeVerifier,
		{ signal: AbortSignal.timeout(OAUTH_REQUEST_TIMEOUT_MS) },
	)

	const result = await oauth2.processAuthorizationCodeOAuth2Response(
		authorizationServer as oauth2.AuthorizationServer,
		client,
		response,
	)
	if (oauth2.isOAuth2Error(result)) {
		const oauthErrorContext = buildOAuthErrorContext(result)

		Sentry.captureException(
			new Error("[OAuthClient] requestAccessToken(): Processing authorization code failed"),
			{
				captureContext: {
					extra: {
						currentUrl: getSafeUrlContext(currentUrl),
						oauth_result: oauthErrorContext,
					},
				},
			},
		)

		throw new Error("Processing authorization code failed")
	}

	analytics.trackEvent({ label: "auth_success", type: "event" })
	setPSAccessToken(result)
}

const resetOAuthClient = () => {
	authorizationServer = null
	store.set(isRefreshingTokenAtom, false)
	refreshPromise = null

	clearPSAccessToken()
	clearPkceStore()
}

const loginToPS = async (options?: { appState: { returnTo: string } }) => {
	const url = await getAuthorizationUrl()
	window.open(url, "_self")
	store.set(returnToAtom, options?.appState?.returnTo ?? null)
}

const logoutFromPS = (options?: { logoutParams: { returnTo: string } }) => {
	deactivateAmplitudeUntilNextUser()
	resetOAuthClient()
	if (options?.logoutParams?.returnTo) {
		window.location.replace(options.logoutParams.returnTo)
	}
}

export {
	// State
	accessTokenAtom,
	isRefreshingTokenAtom,
	isPSAuthenticatedAtom,
	returnToAtom,
	// Actions
	getPSAccessToken,
	setPSAccessToken,
	refreshPSAccessToken,
	discoverAuthorizationServer,
	getAuthorizationUrl,
	requestPSAccessToken,
	clearPSAccessToken,
	resetOAuthClient,
	loginToPS,
	logoutFromPS,
	// PKCE internals, exported for tests
	savePkceVerifier,
	consumePkceVerifier,
	wasPkceEntryExpired,
	MAX_PKCE_TOMBSTONES,
}
